import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { HourglassIcon } from 'lucide-react';
import React from 'react';

import { Loader } from '@/components/Loader';
import { Container } from '@/components/legacy-ui';
import { Button } from '@/components/ui/button';
import { JobStatus } from '@/types';
import { cn } from '@/utils/classname';

import { JobMonitoringSectionProps } from './types';

/**
 * JobMonitoringSection - Complete jobs monitoring section component
 *
 * Displays job execution statistics in a bordered section with:
 * - Section header with title
 * - Horizontal layout of status badges with icons
 * - Status indicators with color coding (success, failed, running, queued)
 * - Localized number formatting
 * - Loading states
 *
 * @example
 * // Basic usage
 * <JobMonitoringSection
 *   stats={{
 *     [JobStatus.Pass]: 150,
 *     [JobStatus.Fail]: 5,
 *     [JobStatus.Running]: 3,
 *     [JobStatus.Queued]: 12,
 *   }}
 *   isLoading={false}
 * />
 *
 * @example
 * // With custom title
 * <JobMonitoringSection
 *   stats={jobStats}
 *   title="Scan Jobs Status"
 * />
 */
export const JobMonitoringSection: React.FC<JobMonitoringSectionProps> = ({
  stats = {
    [JobStatus.Pass]: 0,
    [JobStatus.Fail]: 0,
    [JobStatus.Running]: 0,
    [JobStatus.Queued]: 0,
  },
  isLoading = false,
  title = 'Jobs Monitoring',
  className,
  onViewAll,
  onStatusClick,
}) => {
  /**
   * Handle keyboard interaction for status clicks
   */
  const handleKeyDown = (e: React.KeyboardEvent, status: JobStatus) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onStatusClick?.(status);
    }
  };

  return (
    <Container variant="emphasized" className={cn(className)}>
      {/* Section Header */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-xl font-bold">{title}</h3>
        {onViewAll && (
          <Button variant="link" onClick={onViewAll} className="h-auto p-0">
            View All Jobs →
          </Button>
        )}
      </div>

      {/* Job Status Indicators */}
      <Loader className="min-h-12" isLoading={isLoading}>
        <div className="bg-chariot-background-secondary flex flex-wrap items-center gap-6 rounded-md px-4 py-2 text-sm">
          {/* Success Status */}
          <div
            className={cn(
              'flex items-center gap-2',
              onStatusClick &&
                'cursor-pointer transition-opacity hover:opacity-80'
            )}
            onClick={() => onStatusClick?.(JobStatus.Pass)}
            onKeyDown={e => handleKeyDown(e, JobStatus.Pass)}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <div className="bg-chariot-stroke-info size-3 rounded-full"></div>
            <span>
              Success: {(stats?.[JobStatus.Pass] ?? 0).toLocaleString()}
            </span>
          </div>

          {/* Failed Status */}
          <div
            className={cn(
              'flex items-center gap-2',
              onStatusClick &&
                'cursor-pointer transition-opacity hover:opacity-80'
            )}
            onClick={() => onStatusClick?.(JobStatus.Fail)}
            onKeyDown={e => handleKeyDown(e, JobStatus.Fail)}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <div className="bg-chariot-stroke-critical size-3 rounded-full"></div>
            <span>
              Failed: {(stats?.[JobStatus.Fail] ?? 0).toLocaleString()}
            </span>
          </div>

          {/* Running Status */}
          <div
            className={cn(
              'flex items-center gap-2',
              onStatusClick &&
                'cursor-pointer transition-opacity hover:opacity-80'
            )}
            onClick={() => onStatusClick?.(JobStatus.Running)}
            onKeyDown={e => handleKeyDown(e, JobStatus.Running)}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <ArrowPathIcon className="size-4" />
            <span>
              Running: {(stats?.[JobStatus.Running] ?? 0).toLocaleString()}
            </span>
          </div>

          {/* Queued Status */}
          <div
            className={cn(
              'flex items-center gap-2',
              onStatusClick &&
                'cursor-pointer transition-opacity hover:opacity-80'
            )}
            onClick={() => onStatusClick?.(JobStatus.Queued)}
            onKeyDown={e => handleKeyDown(e, JobStatus.Queued)}
            role={onStatusClick ? 'button' : undefined}
            tabIndex={onStatusClick ? 0 : undefined}
          >
            <HourglassIcon className="size-4" />
            <span>
              Queued: {(stats?.[JobStatus.Queued] ?? 0).toLocaleString()}
            </span>
          </div>
        </div>
      </Loader>
    </Container>
  );
};
